import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import Spinner from '../components/Spinner';
import { FormFieldRenderer } from '../components/lead-capture/FormFieldRenderer';
import { leadFormsApi } from '../api/leadFormsApi';
import { LeadForm } from '../types/leadForms';
import { CheckCircle2, X } from 'lucide-react';
import { FormDisclaimer } from './lead-capture/FormDisclaimer';
import ReCAPTCHA from 'react-google-recaptcha';
import RecaptchaCheckbox from '../components/RecaptchaCheckbox';

interface PublicFormProps {
  embed?: boolean;
}

export default function PublicForm({ embed = false }: PublicFormProps) {
  const { slug } = useParams();

  const [form, setForm] = useState<LeadForm | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState('');
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  useEffect(() => {
    const load = async () => {
      try {
        if (!slug) return;

        const data = await leadFormsApi.getPublicForm(slug);

        setForm({
            ...data,
            fields: (data.fields ?? []).map((field: any) => ({
                id: field.id,
                formId: field.formId,

                label: field.label,
                placeholder: field.placeholder ?? '',
                helpText: field.helpText ?? '',

                type: field.fieldType,
                required: field.isRequired,

                options: field.optionsJson
                ? JSON.parse(field.optionsJson)
                : [],

                displayOrder: field.displayOrder,
            })),
        });
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [slug]);

  const isEmptyValue = (value: unknown) => {
    if (value === undefined || value === null) {
      return true;
    }

    if (typeof value === 'string') {
      return !value.trim();
    }

    if (typeof value === 'boolean') {
      return !value;
    }

    if (Array.isArray(value)) {
      return value.length === 0;
    }

    return false;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!slug || !form) {
      return;
    }

    const nextErrors: Record<string, string> = {};

    form.fields.forEach((field) => {
      if (!field.required) {
        return;
      }

      const value = values[field.id];

      if (isEmptyValue(value)) {
        nextErrors[field.id] = `${field.label} is required.`;
      }
    });

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    if (!captchaToken) {
      setSubmitError('Please complete the CAPTCHA verification.');
      return;
    }

    try {
      setSubmitting(true);
      setSubmitError('');

      await leadFormsApi.submitPublicForm(
        slug,
        { ...values, captchaToken }
      );

      setSubmitted(true);
    } catch (err) {
      const apiError = err as { code?: string; message?: string };
      setSubmitError(apiError.code === 'CAPTCHA_FAILED'
        ? 'Please complete the CAPTCHA verification.'
        : apiError.message || 'Unable to submit the form. Please try again.');
    } finally {
      setSubmitting(false);
      setCaptchaToken(null);
      recaptchaRef.current?.reset();
    }
  };

  const closeSuccess = () => {
    setSubmitted(false);
    setValues({});
    setErrors({});
  };

  if (loading) {
    return (
      <div
        className={`flex items-center justify-center ${
          embed ? 'min-h-[300px]' : 'min-h-screen'
        }`}
      >
        <Spinner />
      </div>
    );
  }

  if (!form) {
    return (
      <div
        className={`flex items-center justify-center px-4 ${
          embed ? 'min-h-[300px]' : 'min-h-screen'
        }`}
      >
        <div className="rounded-2xl bg-white p-8 text-center shadow-lg">
          <h2 className="text-2xl font-bold text-slate-900">
            Form Not Found
          </h2>

          <p className="mt-2 text-slate-600">
            The form you're looking for may no longer be available.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={
        embed
          ? 'relative w-full p-3 md:p-4'
          : 'relative min-h-screen w-full self-stretch px-4 py-8 md:px-6'
      }
      style={{
        backgroundColor: form.secondaryColor,
        backgroundImage: form.backgroundImageUrl
          ? `url(${form.backgroundImageUrl})`
          : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div className="mx-auto max-w-2xl">
        {/* Hero Section */}
        <div
          className={`rounded-2xl text-white ${
            embed ? 'p-5' : 'p-8 shadow-xl'
          }`}
          style={{
            backgroundColor: form.primaryColor,
          }}
        >
          <h1
            className={`font-bold text-white ${
              embed
                ? 'text-2xl md:text-3xl'
                : 'text-3xl md:text-4xl'
            }`}
          >
            {form.headerTitle}
          </h1>

          <p className="mt-3 text-sm md:text-base opacity-90">
            {form.headerDescription}
          </p>
        </div>

        {/* Form Card */}
        <form
          onSubmit={handleSubmit}
          className={`rounded-2xl bg-white ${
            embed
              ? 'mt-4 p-5'
              : 'mt-6 p-6 shadow-xl md:p-8'
          }`}
        >
          {/* <p className="mb-6 text-sm text-slate-500">
            Fields marked with{' '}
            <span className="font-semibold text-red-600">
              *
            </span>{' '}
            are required.
          </p> */}

          <div className="space-y-5">
            {form.fields
              ?.sort(
                (a, b) =>
                  (a.displayOrder ?? 0) -
                  (b.displayOrder ?? 0)
              )
              .map((field) => (
                <div key={field.id}>
                  <FormFieldRenderer
                    field={field}
                    accentColor={form.accentColor}
                    value={values[field.id]}
                    onChange={(value) => {
                      setValues((current) => ({
                        ...current,
                        [field.id]: value,
                      }));

                      if (errors[field.id]) {
                        setErrors((current) => {
                          const next = { ...current };
                          delete next[field.id];
                          return next;
                        });
                      }
                    }}
                  />

                  {errors[field.id] && (
                    <p className="mt-1 text-xs font-medium text-red-600">
                      {errors[field.id]}
                    </p>
                  )}
                </div>
              ))}
          </div>
          {(form.disclaimer || form.privacyPolicyUrl) && (
            <div className="mt-6">
              <FormDisclaimer
                disclaimer={form.disclaimer}
                privacyPolicyUrl={form.privacyPolicyUrl}
                accentColor={form.accentColor}
              />
            </div>
          )}
          <div className="mt-6">
            <RecaptchaCheckbox ref={recaptchaRef} onChange={(token) => { setCaptchaToken(token); setSubmitError(''); }} disabled={submitting} />
            {submitError && <p className="mt-2 text-sm text-red-600">{submitError}</p>}
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="mt-8 w-full rounded-lg px-5 py-4 text-base font-semibold text-white transition-all hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-70"
            style={{
              backgroundColor: form.buttonColor,
            }}
          >
            {submitting
              ? 'Submitting...'
              : form.submitButtonText}
          </button>
        </form>
        {submitted && (
          <div
            className={
              embed
                ? 'absolute inset-0 z-50 flex items-center justify-center bg-strivre-navy/40 p-4 backdrop-blur-sm'
                : 'fixed inset-0 z-50 flex items-center justify-center bg-strivre-navy/40 p-4 backdrop-blur-sm'
            }
            onClick={closeSuccess}
          >
            <div
              className="relative w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={closeSuccess}
                aria-label="Close"
                className="absolute right-4 top-4 rounded-lg p-2 text-slate-400 transition-colors hover:bg-strivre-softgrey hover:text-strivre-burgundy"
              >
                <X size={18} />
              </button>

              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50">
                <CheckCircle2
                  size={34}
                  className="text-emerald-600"
                />
              </div>

              <h2 className="mt-5 text-2xl font-bold text-strivre-navy">
                Thank You!
              </h2>

              <p className="mt-3 text-sm leading-6 text-strivre-mutedtext">
                {form.successMessage}
              </p>

              <button
                type="button"
                onClick={closeSuccess}
                className="mt-6 w-full rounded-lg px-5 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90"
                style={{
                  backgroundColor: form.buttonColor,
                }}
              >
                OK
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}